export type SampleKind = 'timber' | 'concrete' | 'steel' | 'glass' | 'brick'

export interface Material {
    id: SampleKind
    name: string
    detail: string
    carbon: number
    share: number
    shareLabel: string
    note: string
    sampleLabel: string
}

/**
 * Norrvik working values for embodied carbon, A1 to A3, in kgCO2e per cubic metre, and each
 * material's share of structural mass across the portfolio.
 */
export const MATERIALS: Array<Material> = [
    {
        id: 'timber',
        name: 'Timber',
        detail: 'Glulam and cross-laminated',
        carbon: 190,
        share: 46,
        shareLabel: 'Timber',
        note: 'Our first choice for frames, floors and roofs up to eight storeys. Glulam columns and CLT slabs arrive cut to the millimetre from the scanned model, go up quickly and dry, and keep their carbon locked in for the life of the building. We protect them from water with detailing rather than coatings.',
        sampleLabel: 'Scanned sample of a glulam section: long grain lines with growth rings at the end.'
    },
    {
        id: 'concrete',
        name: 'Concrete',
        detail: 'Low-carbon mix',
        carbon: 240,
        share: 27,
        shareLabel: 'Concrete',
        note: 'Used where mass or water resistance is the point: foundations, cores, pools and ground slabs. We specify mixes with at least half of the cement replaced by slag or calcined clay, and size every element from the measured loads rather than rules of thumb.',
        sampleLabel: 'Scanned sample of a concrete core: dense scatter of aggregate with a few voids.'
    },
    {
        id: 'steel',
        name: 'Steel',
        detail: 'Recycled',
        carbon: 4800,
        share: 14,
        shareLabel: 'Steel',
        note: 'For long spans and slender pavilions, such as the truss over Ostra Cultural Hall and the Ribeira roof. We use electric-arc steel with high recycled content, bolt rather than weld so it can come apart again, and keep the tonnage as low as the spans allow.',
        sampleLabel: 'Scanned sample of a steel lattice: a regular grid of nodes and diagonal members.'
    },
    {
        id: 'glass',
        name: 'Glass',
        detail: 'Laminated',
        carbon: 3400,
        share: 9,
        shareLabel: 'Glass',
        note: 'Placed where daylight earns it, guided by the daylight model rather than the elevation. Laminated units with warm-edge spacers and frames sized to be reglazed, so a failed seal means a new pane, not a new facade.',
        sampleLabel: 'Scanned sample of a laminated glass unit: parallel planes with an edge spacer.'
    },
    {
        id: 'brick',
        name: 'Brick',
        detail: 'Reclaimed',
        carbon: 90,
        share: 4,
        shareLabel: 'Other, including reclaimed brick',
        note: 'Reclaimed from demolition within 200 km, cleaned, sorted and relaid in lime mortar so it can be reclaimed again. We use it for ground floors, garden walls and repairs to existing facades, where it carries almost no new carbon.',
        sampleLabel: 'Scanned sample of reclaimed brickwork: stretcher bond courses with mortar joints.'
    }
]

export const MAX_CARBON = Math.max(...MATERIALS.map(material => material.carbon))

export interface StructureOption {
    id: string
    label: string
    perM2: number
}

/**
 * Typical embodied carbon per m² of floor area for a whole building, kgCO2e.
 */
export const STRUCTURE_OPTIONS: Array<StructureOption> = [
    { id: 'concrete', label: 'All concrete', perM2: 520 },
    { id: 'steel', label: 'Steel frame', perM2: 380 },
    { id: 'mixed', label: 'Mixed', perM2: 300 },
    { id: 'timber', label: 'Timber first', perM2: 210 }
]

export const AREA_MIN = 1000
export const AREA_MAX = 15000
export const AREA_STEP = 100
